import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import {
  backtestProposal,
  createGoal,
  createProposal,
  deleteGoal,
  deleteProposal,
  downloadProposalReport,
  fetchGoalHoldings as fetchGoalAvailableHoldings,
  fetchGoalProposals,
  fetchGoals,
  fetchProposalReportData,
  fetchProposals,
  generatePropoposalReport,
  getGoal,
  getProposal,
  syncGoalHoldings,
  updateGoal,
  updateProposal,
  updatePropsalFunds,
} from "../../api/clients/proposal";
import {
  CreateGoalRequestPayload,
  CreateProposalRequestPayload,
  CreateProposalFund,
  Goal,
  PaginatedResponse,
} from "../../types";
import { GOAL_OPTIONS } from "../../constants";

export const goalsQueryOptions = (
  variables: { clientId: number },
  options?: { enabled?: boolean }
) =>
  queryOptions({
    queryKey: ["goals", variables.clientId],
    queryFn: () => fetchGoals(variables.clientId),
    ...options,
  });

export function useGoals(
  variables: { clientId: number },
  options?: { enabled?: boolean }
) {
  return useQuery(goalsQueryOptions(variables, options));
}

export function useCreateGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateGoalRequestPayload) => createGoal(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["goals"],
      });
    },
  });
}

export function useUpdateGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: CreateGoalRequestPayload;
    }) => updateGoal(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["goal"],
      });
      queryClient.invalidateQueries({
        queryKey: ["goals"],
      });
    },
  });
}

export function useGoal(
  variables: { id: number },
  options: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["goal", variables.id],
    queryFn: () => getGoal(variables.id),
    ...options,
  });
}

export function useDefaultGoalName(prefix?: string) {
  const queryClient = useQueryClient();

  return useCallback(
    (type: string) => {
      let i = 1;
      const goalType = GOAL_OPTIONS.find((g) => g.value === type)?.label;
      const goals = queryClient.getQueryData<PaginatedResponse<Goal>>([
        "goals",
      ])?.results;
      const names = new Set(goals?.map((g) => g.name) || []);
      const suffix = type === "CUSTOM" ? "goal" : "";
      const name = [prefix, goalType, suffix].filter(Boolean).join(" ");
      if (!names.has(name)) return name;
      // Safety guard to avoid infinite loop
      while (names.has(`${name} (${i})`) && i < 1000) {
        i += 1;
      }
      return `${name} (${i})`;
    },
    [queryClient]
  );
}

export function useGoalAvailableHoldings(
  variables: { id: number },
  options: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["goal", variables.id, "holdings"],
    queryFn: () => fetchGoalAvailableHoldings(variables.id),
    ...options,
  });
}

export function useSyncGoalHoldings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: syncGoalHoldings,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["goals"],
      });
      queryClient.invalidateQueries({
        queryKey: ["goal", variables.id],
      });
    },
  });
}

export function useProposals(
  variables: { clientId: number },
  options?: { enabled?: boolean }
) {
  return useQuery({
    // "list" / "detail" keep a client id from colliding with a proposal id
    queryKey: ["proposals", "list", variables.clientId],
    queryFn: () => fetchProposals(variables.clientId),
    ...options,
  });
}

export const proposalQueryOption = (
  variables: { proposalId: number },
  options?: { enabled?: boolean }
) =>
  queryOptions({
    queryKey: ["proposals", "detail", variables.proposalId],
    queryFn: () => getProposal(variables.proposalId),
    ...options,
  });

export function useProposal(
  variables: { proposalId: number },
  options?: { enabled?: boolean }
) {
  return useQuery(proposalQueryOption(variables, options));
}

export function useCreateProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateProposalRequestPayload) =>
      createProposal(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["proposals", "list", variables.client],
      });
    },
  });
}

export function useUpdateProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: Partial<CreateProposalRequestPayload>;
    }) => updateProposal(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["proposals", "list", variables.payload.client],
      });
    },
  });
}

export function useUpdateProposalFunds() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      proposalId,
      proposalFunds,
    }: {
      proposalId: number;
      proposalFunds: CreateProposalFund[];
    }) => updatePropsalFunds(proposalId, proposalFunds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["proposals"],
      });
    },
  });
}

export function useDeleteGoal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteGoal,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["goals"],
      });
    },
  });
}

export function useDeleteProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteProposal,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["proposals"],
      });
    },
  });
}

export const proposalReportOptions = (
  variables: { proposalId: number },
  options?: { enabled?: boolean }
) =>
  queryOptions({
    queryKey: ["proposal-report-data", variables.proposalId],
    queryFn: () => fetchProposalReportData(variables.proposalId),
    ...options,
  });

export function useProposalReportData(
  variables: { proposalId: number },
  options?: { enabled?: boolean }
) {
  return useQuery(proposalReportOptions(variables, options));
}

export function useGoalProposals(
  variables: { goalId: number },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["goal-proposals", variables.goalId],
    queryFn: () => fetchGoalProposals(variables.goalId),
    ...options,
  });
}

export function useGenerateProposalReport() {
  return useMutation({
    mutationFn: generatePropoposalReport,
  });
}

export function useDownloadProposalReport() {
  return useMutation({
    mutationFn: downloadProposalReport,
  });
}

export function useProposalBacktest(
  variables: Parameters<typeof backtestProposal>[0]
) {
  return useQuery({
    queryKey: ["proposals", "backtest", { ...variables }],
    queryFn: () => backtestProposal(variables),
  });
}
